// The library namespaces every layout it persists under this prefix; both the
// current and legacy key shapes build on it.
const STORAGE_KEY_PREFIX = 'react-resizable-panels';

type LegacyLayoutRecord = Record<string, { layout?: number[] }>;

/**
 * Reads a layout saved by the pre-v4 library, which the library's own
 * migration cannot restore here: it keyed each combination by panel ids sorted
 * alphabetically while storing the sizes in panel order, so zipping the key
 * against the sizes mismatches whenever the two orders differ. Taking the
 * order from the rendered panels instead pairs them correctly.
 *
 * Safe to delete once these layouts have aged out of users' browsers.
 */
export const readLegacyLayout = (
  groupId: string,
  panelIds: string[] | undefined,
): Record<string, number> | undefined => {
  if (!panelIds) return undefined;
  try {
    const stored = localStorage.getItem(`${STORAGE_KEY_PREFIX}:${groupId}`);
    if (!stored) return undefined;
    const record = JSON.parse(stored) as LegacyLayoutRecord;
    const sortedKey = [...panelIds]
      .sort((a, b) => a.localeCompare(b))
      .join(',');
    const layout = record[sortedKey]?.layout;
    if (!Array.isArray(layout) || layout.length !== panelIds.length) {
      return undefined;
    }
    return Object.fromEntries(panelIds.map((id, index) => [id, layout[index]]));
  } catch {
    return undefined;
  }
};

/**
 * Reads the layout persisted for a group outside React — e.g. to size a
 * loading skeleton to match where the real panels will land. Tries the key
 * `useDefaultLayout` writes (react-resizable-panels:<group>:<panelIds…> →
 * {panelId: size}), then falls back to the pre-v4 record above, so the
 * storage-format knowledge stays in this one file.
 */
export const readPersistedGroupLayout = (
  groupId: string,
  panelIds: string[],
): Record<string, number> | undefined => {
  try {
    const stored = localStorage.getItem(
      `${STORAGE_KEY_PREFIX}:${[groupId, ...panelIds].join(':')}`,
    );
    if (stored) {
      const layout = JSON.parse(stored) as Record<string, unknown>;
      if (panelIds.every((id) => typeof layout[id] === 'number')) {
        return layout as Record<string, number>;
      }
    }
  } catch {
    // fall through to the legacy read
  }
  return readLegacyLayout(groupId, panelIds);
};
